import React, { FC, useEffect, useState, useMemo } from "react";
import { Table, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { usePurchaseRoModel } from "../purchaseRoModel";
import { IPurchaseRoDetail } from "../purchaseRoType"
import YhTag from "@/components/YhTag";
import { useDictModel } from "@/views/base/dict/dictModel";
import { momentFormat, formatNum } from "@/utils/utils";
import useNavModel from "@/model/navModel";
import { tableSize } from "@/utils/constants";
import YhTootip from "@/components/YhTootip";
import { roleColumnModel } from "@/utils/roleColumn";
import useGlobalModel from "@/model/globalModel";

type statusType = {
    "purchase-return.create": string,
    "purchase-return.stock-io": string,
    "purchase-return.settle": string,
    "purchase-return.change": string,
    [key: string]: any
}

export function DataTable() {
    const {
        dataSource, selectedRowKeys, pgCfg, descRows, descTotal, rowsDescTotal,
        loadData, setSelectedRowKeys, setDescRows, reset
    } = usePurchaseRoModel();
    const { getTreeNodeName } = useDictModel();
    const { selectedNavId, replaceNav, findNav } = useNavModel();
    const { user: { roles } } = useGlobalModel();
    const { getRoleColumns } = roleColumnModel();

    const onLook = (code: string) => {
        let supplierEditKey = "11-3-3";
        replaceNav(
            selectedNavId,
            supplierEditKey,
            { id: code, mode: "look", },
            true
        );
    }
    useEffect(() => {
        loadData();
    }, [])

    const statusColor: statusType = {
        "purchase-return.create": "blue",
        "purchase-return.stock-io": "cyan",
        "purchase-return.settle": "green",
        "purchase-return.change": "red",
    }
    const defaultColumns: ColumnsType<IPurchaseRoDetail> = [
        {
            title: '序号',
            fixed: "left",
            width: 60,
            render: (t, r, i) => i + 1
        }, {
            title: '退货单号',
            width: 120,
            dataIndex: 'returnCode',
            render: (text: any, record: any,) => <YhTootip text={text} onClick={() => onLook(text)} />,
            // render: (text: any) => <Tooltip placement="topLeft" title={text}><p className="ellipsis click-span" onClick={(e) => {e.stopPropagation();onLook(text)}}>{text}</p></Tooltip>,

        }, {
            title: '单据状态',
            width: 80,
            dataIndex: 'status',
            render: (text) => <YhTag color={statusColor[text]} text={getTreeNodeName(text)} />,
        }, {
            title: '支付状态',
            width: 80,
            dataIndex: 'payStatus',
            render: (text) => getTreeNodeName(text)
        }, {
            title: '退货数量',
            width: 80,
            dataIndex: 'totalNum',

        }, {
            title: '已收金额',
            width: 80,
            dataIndex: 'paidAmount',
            render: (text) => `￥${formatNum(text)}`

        }, {
            title: '结算类型',
            width: 80,
            dataIndex: 'payType',
            render: (text) => getTreeNodeName(text)
        }, {
            title: '结算方式',
            width: 80,
            dataIndex: 'payMethodName',
            // render: (text: any) => <Tooltip placement="topLeft" title={text}><p className="ellipsis">{getTreeNodeName(text) ? getTreeNodeName(text) : text}</p></Tooltip>,
            render: (text: any, record: any) => <Tooltip placement="topLeft" title={record?.settleDetails?.[0]?.payMethodName ?? record?.settleDetails?.[0]?.accountName}><p className="ellipsis">{record?.settleDetails?.[0]?.payMethodName ?? record?.settleDetails?.[0]?.accountName}</p></Tooltip>,
        }, {
            title: '单据类型',
            width: 80,
            dataIndex: 'documentTypeName',
            render: text => text === '' || text === null ? "普通" : text
        }, {
            title: '发票类型',
            width: 160,
            dataIndex: 'invoiceTypeName',
        }, {
            title: '联系人',
            width: 160,
            dataIndex: 'contact',
            render: (text: any) => <YhTootip text={text} />,

        }, {
            title: '联系电话',
            width: 95,
            dataIndex: 'phone',
        }, {
            title: '制单员',
            width: 90,
            dataIndex: 'createUser',
        }, {
            title: '制单时间',
            width: 130,
            dataIndex: 'createTime',
            render: (text) => momentFormat(text)
        }, {
            title: '结算员',
            width: 130,
            dataIndex: 'reckoner',
        }, {
            title: '结算时间',
            width: 130,
            dataIndex: 'settleTime',
            render: (text) => momentFormat(text)
        }, {
            title: '库管',
            width: 100,
            dataIndex: 'storekeeper',
        }, {
            title: '出库时间',
            width: 130,
            dataIndex: 'stockIoTime',
            render: (text) => momentFormat(text)
        }, {
            title: '核销时间',
            width: 130,
            dataIndex: 'writeOffTime',
            render: (text) => momentFormat(text)
        }, {
            title: '作废原因',
            width: 120,
            dataIndex: 'cancelReason',
        }, {
            title: '备注',
            width: 120,
            dataIndex: 'note',
        }, {
            title: '供应商',
            width: 160,
            dataIndex: 'supplierName',
            render: (text: any) => <YhTootip text={text} />,
            fixed: "right"
        }, {
            title: '采退金额',
            width: 80,
            dataIndex: 'payableAmount',
            fixed: "right",
            render: (text) => `￥${formatNum(text)}`
        }, {
            title: '打印次数',
            width: 90,
            fixed: "right",
            align: "center",
            dataIndex: 'printingTimes',
        },
        {
            title: '所属站点',
            width: 250,
            fixed: "right",
            dataIndex: 'shopName',
            render: (text: any) => <YhTootip text={text} style={{ width: 250 }} />,
        }
    ];

    const roleColumns = useMemo(() => {
        //根据可视化配置处理需要展示相关列
        const params = { "code": "purchaseReturn", "roleCodes": roles };
        return getRoleColumns("purchaseReturn", "主单", defaultColumns, params);
    }, [roles, dataSource]);
    //点击行选高亮
    const onRow = (record: IPurchaseRoDetail) => {
        let { id } = record as { id: string }
        let findIndexDesc = descRows.findIndex((item) => (item.id === id))
        let newDescRows = [...descRows];
        let newSelectedRowKeys = [...selectedRowKeys];

        if (findIndexDesc === -1) {
            //统计数据
            newDescRows.push(record)
            setDescRows(newDescRows)
            //表格选中行Key值
            newSelectedRowKeys.push(id);
            setSelectedRowKeys(newSelectedRowKeys);
        } else {
            //统计数据
            newDescRows.splice(findIndexDesc, 1);
            setDescRows(newDescRows);
            //表格选中行Key值
            newSelectedRowKeys.splice(findIndexDesc, 1);
            setSelectedRowKeys(newSelectedRowKeys);
        }
    };

    const onRightClick = (record: IPurchaseRoDetail) => {
        setDescRows([record])
        setSelectedRowKeys([record.id as string])
    }
    return (
        <Table
            bordered
            style={{ cursor: "pointer" }}
            size={tableSize}
            rowKey={record => record.id as string}
            rowSelection={{
                type: "checkbox",
                onChange: (selectedRowKeys: React.Key[], selectedRows: IPurchaseRoDetail[]) => {
                    setSelectedRowKeys(selectedRowKeys);
                    setDescRows(selectedRows)
                },
                selectedRowKeys
            }}
            columns={roleColumns}
            dataSource={dataSource}
            pagination={{
                showSizeChanger: true,
                ...pgCfg,
                defaultPageSize: 25,
                pageSizeOptions: ['15', '25', '50', '100', '200', '500'],
                position: ["bottomRight"],
                showTotal: () => `共${pgCfg.total}条`,
                onChange: (pageNum, pageSize) => {
                    loadData({ pageNum, pageSize }).then();
                }
            }}
            onRow={record => ({
                onClick: () => onRow(record),
                onContextMenu: (e) => {
                    e.preventDefault();
                    onRightClick(record)

                }

            })}
            scroll={{ x: 'max-content', y: 'calc(100vh - 370px)' }}
            summary={() => (
                <Table.Summary fixed>
                    <Table.Summary.Row className="table-total-box">
                        <Table.Summary.Cell index={0}>
                            <div className="title">合计</div>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} />
                        <Table.Summary.Cell index={2} />
                        <Table.Summary.Cell index={3} />
                        <Table.Summary.Cell index={4} />
                        <Table.Summary.Cell index={5}>
                            <span className="red">{selectedRowKeys.length === 0 ? descTotal.totalNum : rowsDescTotal.totalNum}</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6} />
                        {/* >
                            <span className="red">{formatNum(selectedRowKeys.length === 0 ? descTotal.paidAmount : rowsDescTotal.paidAmount)}</span>
                        </Table.Summary.Cell> */}
                        <Table.Summary.Cell index={7} />
                        <Table.Summary.Cell index={8} />
                        <Table.Summary.Cell index={9} />
                        <Table.Summary.Cell index={10} />
                        <Table.Summary.Cell index={11} />
                        <Table.Summary.Cell index={12} />
                        <Table.Summary.Cell index={13} />
                        <Table.Summary.Cell index={14} />
                        <Table.Summary.Cell index={15} />
                        <Table.Summary.Cell index={16} />
                        <Table.Summary.Cell index={17} />
                        <Table.Summary.Cell index={18} />
                        <Table.Summary.Cell index={19} />
                        <Table.Summary.Cell index={20} />
                        <Table.Summary.Cell index={21} />
                        <Table.Summary.Cell index={22} />
                        <Table.Summary.Cell index={23} />
                        {/* >
                            <span className="red">{formatNum(selectedRowKeys.length === 0 ? descTotal.totalAmount : rowsDescTotal.totalAmount)}</span>
                        </Table.Summary.Cell> */}
                        <Table.Summary.Cell index={24} />
                        <Table.Summary.Cell index={25} />
                    </Table.Summary.Row>
                </Table.Summary>
            )}
        />
    )
}
